import { Elements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { Tabs } from "antd"
import React, { useEffect, useState } from "react"
import { Edit as EditIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useHistory, useRouteMatch } from "react-router-dom"
import Loading from "~Components/loading"
import SettingsLayout, { SettingsButton } from "~Features/settings/settings.layout"
import { STRIPE_API_KEY } from "~Shared/config/consts"
import { useBillingSettings } from "~Shared/queries/billing.queries"
import { PaymentSettingsTabRoutes, RoutePathEnum } from "~Shared/router/RoutePath.enum"
import useStateIfMounted from "~Shared/utils/hooks/useStateIfMounted"
import { BillingStripeButton } from "./billingStripe.button"

const { TabPane } = Tabs

const stripePromise = loadStripe(STRIPE_API_KEY)

const BillingSettings = React.lazy(() => import("./billingSettings/billingSettings").then((module) => ({ default: module.BillingSettings })))
const BillingPaymentMethodSettings = React.lazy(() => import("./billingPaymentMethodSettings").then((module) => ({ default: module.BillingPaymentMethodSettings })))
const BillingInvoices = React.lazy(() => import("./billingInvoice.list").then((module) => ({ default: module.BillingInvoiceList })))

const BillingContainer = () => {
    const { t } = useTranslation()
    const history = useHistory()

    const routeMatch = useRouteMatch<{ params: string }>("/settings/system/billing/:tab")
    const [activeTab, setActiveTabBasic] = useState<PaymentSettingsTabRoutes>(RoutePathEnum.SETTINGS_BILLING_TAB_PLAN)
    const [isPaymentMethodFormEditable, setIsPaymentMethodFormEditable] = useStateIfMounted(false)
    const { data: billingSettings, isFetching } = useBillingSettings({})

    useEffect(() => {
        if (routeMatch) {
            setActiveTabBasic(`${RoutePathEnum.SETTINGS_BILLING}/${(routeMatch.params as any).tab}` as PaymentSettingsTabRoutes)
        }
    }, [routeMatch])

    const handleEdit = () => {
        if (activeTab === RoutePathEnum.SETTINGS_BILLING_TAB_PAYMENT_METHOD) {
            setIsPaymentMethodFormEditable(true)
        }
    }

    const setActiveTab = (activeTabToSet: PaymentSettingsTabRoutes, isPushHistory = true) => {
        if (isPushHistory) {
            history.push(`${activeTabToSet}`)
        }
        setActiveTabBasic(activeTabToSet)
    }

    if (isFetching || !billingSettings) {
        return <Loading />
    }

    const tabs = [
        {
            title: t("label:payment_settings.plan"),
            key: RoutePathEnum.SETTINGS_BILLING_TAB_PLAN,
            component: <BillingSettings setActiveTab={setActiveTab} />,
        },
        {
            key: RoutePathEnum.SETTINGS_BILLING_TAB_PAYMENT_METHOD,
            title: t("label:payment_settings.payment"),
            component: (
                <Elements
                    stripe={stripePromise}
                    options={{
                        appearance: { variables: { fontFamily: "proxima-nova, sans-serif", fontSizeBase: "15px" } },
                    }}
                >
                    <BillingPaymentMethodSettings isFormEditable={isPaymentMethodFormEditable} setIsFormEditable={setIsPaymentMethodFormEditable} />
                </Elements>
            ),
        },
        {
            key: RoutePathEnum.SETTINGS_BILLING_TAB_INVOICES,
            title: t("label:payment_settings.invoices"),
            component: <BillingInvoices activeTab={activeTab} />,
        },
    ]

    return (
        <SettingsLayout
            titleKey="label:payment_settings.title"
            buttons={
                <>
                    <BillingStripeButton />
                    {activeTab === RoutePathEnum.SETTINGS_BILLING_TAB_PAYMENT_METHOD && <SettingsButton icon={<EditIcon />} onClick={handleEdit} labelKey="action:edit" />}
                </>
            }
        >
            <Tabs activeKey={activeTab} onChange={(key: PaymentSettingsTabRoutes) => setActiveTab(key)}>
                {tabs.map((tab) => (
                    <TabPane tab={tab.title} key={tab.key} className="pb-10">
                        {tab.component}
                    </TabPane>
                ))}
            </Tabs>
        </SettingsLayout>
    )
}

export default React.memo(BillingContainer)
